import axios from 'axios';
import config from '../../../utils/config';
import { O365ConsentStep } from './o365Onboarding.model';

// The public /connect-office365 page is opened by a client admin who isn't signed in to Synapse,
// so it must not use utils/request. That one asks MSAL for a token (opening a login popup), and
// its interceptor toasts and redirects on errors.
const publicClient = axios.create({
  baseURL: config.baseApi,
  headers: { Accept: 'application/json' },
});

// Only ever navigate the browser to Microsoft's own sign-in. The server mints this URL from
// signed, short-lived state, but we still verify it here before calling window.location.assign:
// defence in depth against a compromised/misbehaving API response (or a javascript: URL).
const MICROSOFT_LOGIN_HOST = /^login\.microsoftonline\.(com|us)$/i;

export interface ResolvedO365ConsentLink {
  consentUrl: string;
  consentStep: O365ConsentStep;
}

class ConnectOffice365Service {
  private assertSafeConsentUrl(rawUrl: string): string {
    let parsed: URL;
    try {
      parsed = new URL(rawUrl);
    } catch {
      throw new Error('Received an invalid consent URL.');
    }
    if (parsed.protocol !== 'https:' || !MICROSOFT_LOGIN_HOST.test(parsed.hostname)) {
      throw new Error('Received an unexpected consent URL.');
    }
    return rawUrl;
  }

  public async resolveLink(token: string): Promise<ResolvedO365ConsentLink> {
    const res = await publicClient.get(
      `/o365-onboarding-callback/link/${encodeURIComponent(token)}`
    );
    const { consent_url: consentUrl, consent_step: consentStep } = res.data.body.data;
    return { consentUrl: this.assertSafeConsentUrl(consentUrl), consentStep };
  }
}

export default new ConnectOffice365Service();
